import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';

/**
 * S16 — Cleanup confirmation modal.
 *
 * Lists the cleanup plan + skipped workloads BEFORE the operator confirms.
 * This is the ONLY entry point — cleanup never auto-fires (operator-explicit
 * invariant from rightsizing_cleanup.py).
 *
 * Backend:
 *   GET  /api/v1/clusters/{cid}/rightsizing/cleanup-plan
 *        → { candidates: [...], skips: [...] }
 *   POST /api/v1/clusters/{cid}/rightsizing/cleanup
 *        body: { confirm: "REVERT", initiator_user_id: <self> }
 */

const SKIP_LABELS = {
  oom_protection_active:      { label: 'OOM-protected', tone: 'destructive' },
  critical_priority_protected:{ label: 'Critical priority', tone: 'destructive' },
  excluded_namespace:         { label: 'Excluded namespace', tone: 'secondary' },
  no_lkg_snapshot:            { label: 'No baseline', tone: 'secondary' },
  already_at_lkg:             { label: 'Already at baseline', tone: 'secondary' },
};

export default function CleanupConfirmationModal({ clusterId, open, onClose, onSubmitted }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setPlan(null); setConfirmText(''); setError(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/v1/clusters/${clusterId}/rightsizing/cleanup-plan`,
        );
        setPlan(r.data || {});
      } catch (err) {
        setError(err?.response?.data?.detail || String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, clusterId]);

  const submit = async () => {
    if (confirmText !== 'REVERT') return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/v1/clusters/${clusterId}/rightsizing/cleanup`,
        { confirm: 'REVERT' },
      );
      if (onSubmitted) onSubmitted(r.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const candidates = plan?.candidates || [];
  const skips = plan?.skips || [];
  const oomProtected = skips.filter(s => s.reason === 'oom_protection_active');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Revert applied rightsizing patches
          </DialogTitle>
          <DialogDescription>
            Reverts all eligible workloads to their last-known-good resource specs.
            This is operator-explicit only; auto-disable safety triggers never call this.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="text-sm text-gray-500 p-4">Loading plan…</div>}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded">{error}</div>
          )}

          {plan && (
            <>
              <div className="grid grid-cols-3 gap-3 my-4">
                <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{candidates.length}</div>
                  <div className="text-xs text-red-700">will revert</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{oomProtected.length}</div>
                  <div className="text-xs text-green-700">OOM-protected (skipped)</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{skips.length}</div>
                  <div className="text-xs text-gray-700">other skips</div>
                </div>
              </div>

              {oomProtected.length > 0 && (
                <div className="border-2 border-green-300 rounded p-3 bg-green-50 mb-3">
                  <div className="flex items-center gap-2 text-green-800 mb-2 font-semibold">
                    <ShieldCheck className="w-4 h-4" />
                    OOM-protected workloads (safe — will NOT revert)
                  </div>
                  <div className="text-xs text-green-700 space-y-1 max-h-24 overflow-y-auto">
                    {oomProtected.map((s, i) => (
                      <div key={i}>{s.namespace}/{s.workload_name}</div>
                    ))}
                  </div>
                  <div className="text-xs mt-2 text-green-700 italic">
                    These workloads had memory raised to prevent OOMs. Reverting
                    them would re-introduce the incident, so cleanup skips them
                    automatically.
                  </div>
                </div>
              )}

              <div className="border rounded">
                <div className="bg-gray-50 px-3 py-2 text-sm font-semibold border-b">
                  Workloads to revert ({candidates.length})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {candidates.length === 0 ? (
                    <div className="text-sm text-gray-500 italic p-3">Nothing to revert.</div>
                  ) : (
                    candidates.map((c, i) => (
                      <div key={i} className="px-3 py-2 border-b text-sm flex justify-between items-center">
                        <div className="font-mono">{c.namespace}/{c.workload_name}</div>
                        <div className="text-xs text-gray-500">
                          CPU {fmtCpu(c.current_resources?.cpu_request)} →{' '}
                          <span className="text-blue-700 font-medium">
                            {fmtCpu(c.lkg_resources?.cpu_request)}
                          </span>
                          <span className="mx-1">·</span>
                          Mem {fmtBytes(c.current_resources?.memory_request)} →{' '}
                          <span className="text-blue-700 font-medium">
                            {fmtBytes(c.lkg_resources?.memory_request)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {skips.length > oomProtected.length && (
                <details className="mt-3">
                  <summary className="text-sm cursor-pointer text-gray-600">
                    Other skips ({skips.length - oomProtected.length})
                  </summary>
                  <div className="text-xs space-y-1 mt-2 max-h-32 overflow-y-auto">
                    {skips.filter(s => s.reason !== 'oom_protection_active').map((s, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="font-mono">{s.namespace}/{s.workload_name}</span>
                        <Badge variant={SKIP_LABELS[s.reason]?.tone || 'secondary'}>
                          {SKIP_LABELS[s.reason]?.label || s.reason}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {candidates.length > 0 && (
                <div className="mt-4 border-2 border-red-300 rounded p-3 bg-red-50">
                  <div className="flex items-center gap-2 text-red-800 mb-2 font-semibold">
                    <ShieldAlert className="w-4 h-4" />
                    Type <span className="font-mono bg-white px-1">REVERT</span> to confirm:
                  </div>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="REVERT"
                    autoFocus
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={
              loading || submitting || confirmText !== 'REVERT' ||
              !candidates.length
            }
            onClick={submit}
          >
            {submitting ? 'Reverting…' : `Revert ${candidates.length} workloads`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtCpu(milli) {
  if (!milli && milli !== 0) return '?';
  if (typeof milli !== 'number') return String(milli);
  return `${milli}m`;
}

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return '?';
  if (typeof bytes !== 'number') return String(bytes);
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)}Gi`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)}Mi`;
  return `${bytes}`;
}
